import { DEFAULT_SELECTED_BCTX_INFO } from '@monorepo/core-data';
import {
  TBctxItem,
  TClient_BCTX,
  TRESPONSE_BusinessContextData,
  TRESPONSE_AppStateResponse,
  TBctxLevelSyncedState,
  TUserActualsDB,
  TShiftMap,
  TClientWork,
  TSizeMapItem,
  TRESPONSE_RestoreToBeRectifiedCounts,
  TBctxItemWithoutSize,
  TRectifiedBCTXInfo,
  TSizeMap,
  TBctxWithMultipleSize,
  TBctxItemAndSyncedStateSize,
  TBctxHeaderItem,
  TLineMapItem,
  TBctxWipMap,
  TSizeToWipMapItem,
  TSizeInfo,
  TSelectedStationIdAndShiftId,
  TShiftMapItem,
  TBCTXKeysWithoutSize,
  TLineMap,
  TBulkEvent,
  TRESPONSE_ServerDefectives,
  TServerDefectivesDB,
  TDefectiveInfo,
  TDefectiveOperationIssueAndSilhouetteMarkingInfo,
  TQcActionItem,
  TSingleCountEvent,
  TEventCommonDetails,
  TDefectiveResolutionActionType,
  TOperationIssueAndMarkedLocations,
  TCTXDTO,
  IRejectList,
  TMDPointActionItem,
  TAppSyncScreenItem,
  TEventBctxDetails,
  TRESPONSE_AppStateResponseForBackedUpBctx,
  TMDPointGradingPageAction,
  TEventBackgroundMetadata,
  TBgLineMap,
  TActualType,
  TBackgroundEventsSyncingDB,
  TBulkEventBackgroundData,
  TBulkEventWithoutStatus,
  TEventStationDetails,
  TForwardSingleEventWithoutStatus,
  TBgEventsSyncingDBLockDetails,
  TUserActualsDBLockDetails,
  TBCTXKeys,
  TCheckInActionItem,
  TSelectedBctxInfo,
  TRestoreBackedUpBctx,
  TIncompleteBundlesInfoLineMap,
  TNewBctxItemInPOSplit,
  TVariableGroupFieldsInfo,
  TPlanBasedLineMap,
  TPlanBasedLineMapItem,
  TAllMasterdataListsByBctx,
  TLineMapCreationItems,
  TMasterdataListResult,
  TMasterdataItemInfo,
  TAllBctxItems,
  TPastDaysBackgroundEventsSyncInfo,
  TAppIdentifier,
  TBCTXKeysWithDateTime,
  TBctxMinMaxDateTime,
  TBctxLevelTotalCheckins,
} from '@monorepo/types';
import produce from 'immer';
import { BctxViz } from 'libs/types/src/lib/input-app/pre-settings.types';
import moment from 'moment';
import * as _ from 'lodash';

export const BCTXComputationHelper = {
  getDisplayName(item: TBctxItemWithoutSize[], key: TBCTXKeysWithoutSize) {
    const index = item.findIndex(({ k }) => k === key);
    return index > -1 ? item[index].v : '--';
  },
  dashFormateDate: () => {
    const day = new Date();
    const year = day.getFullYear();
    const month = day.getMonth() + 1 < 10 ? '0' + (day.getMonth() + 1) : day.getMonth() + 1;
    const date = day.getDate().toString().length < 2 ? '0' + day.getDate() : day.getDate();
    return year + '-' + month + '-' + date;
  },
  isToday: (datetime: number) => {
    return (
      moment(new Date(datetime)).isSameOrAfter(new Date().setHours(0, 0, 0, 0)) &&
      moment(new Date(datetime)).isSameOrBefore(new Date().setHours(23, 59, 59, 999))
    );
  },
  isTodayOrYesterday: (datetime: number) => {
    const currentTime = new Date().getTime();
    return (
      moment(new Date(datetime)).isSameOrAfter(new Date(currentTime - 86400000).setHours(0, 0, 0, 0)) &&
      moment(new Date(datetime)).isSameOrBefore(new Date(currentTime).setHours(23, 59, 59, 999))
    );
  },
  createBctxItemHeader: (
    bctxItem: TBctxItemWithoutSize[],
    orderKeys: TBCTXKeysWithoutSize[],
    bctxKeysToDisplayMap: Record<TBCTXKeys, string>,
    size?: string,
  ): TBctxHeaderItem[] => {
    const header = orderKeys.map((key) => {
      return {
        label: bctxKeysToDisplayMap[key],
        value: BCTXComputationHelper.getDisplayName(bctxItem, key),
      };
    });
    if (size) {
      header.push({
        label: bctxKeysToDisplayMap['size'],
        value: size,
      });
    }
    return header;
  },
  createMdPointAction: (): TMDPointActionItem[] => {
    return [
      {
        type: 'FTT',
        displayName: 'bctx_computation_helper.mdPass',
        totalActuals: 0,
      },
      {
        type: 'IN_PROGRESS',
        displayName: 'bctx_computation_helper.onHold',
        totalActuals: 0,
      },
      {
        type: 'REJECTED',
        displayName: 'bctx_computation_helper.mdFail',
        totalActuals: 0,
      },
      {
        type: 'TO_BE_RECTIFIED',
        displayName: 'bctx_computation_helper.toBeRectified',
        totalActuals: 0,
      },
    ];
  },
  getDefaultQcActuals: (isMdPoint = false): TQcActionItem[] => {
    return [
      {
        type: 'FTT',
        displayName: isMdPoint ? 'bctx_computation_helper.mdPass' : 'input_app_common.rft',
        totalActuals: 0,
      },
      {
        type: 'DEFECTIVE',
        displayName: isMdPoint ? 'bctx_computation_helper.onHold' : 'bctx_computation_helper.defectives',
        totalActuals: 0,
      },
      {
        type: 'REJECTED',
        displayName: isMdPoint ? 'bctx_computation_helper.mdFail' : 'bctx_computation_helper.rejected',
        totalActuals: 0,
      },
      {
        type: 'REWORKED_FROM_DEFECTIVE',
        displayName: isMdPoint ? 'bctx_computation_helper.toBeRectified' : 'bctx_computation_helper.rectified',
        totalActuals: 0,
      },
    ];
  },
  getDefaultCheckInActuals: (isLineIn: boolean = false): TCheckInActionItem[] => {
    return [
      {
        type: 'CHECK_IN',
        displayName: isLineIn ? 'bctx_computation_helper.lineIn' : 'bctx_computation_helper.checkIn',
        totalActuals: 0,
      },
    ];
  },
  createInitialClientWork: (): TClientWork => {
    return {
      CHECK_IN: {
        actuals: 0,
        displayName: 'bctx_computation_helper.checkIn',
      },
      FTT: {
        actuals: 0,
        displayName: 'bctx_computation_helper.mdPass',
      },
      IN_PROGRESS: {
        actuals: 0,
        displayName: 'bctx_computation_helper.inProgress',
      },
      REJECTED: {
        actuals: 0,
        displayName: 'bctx_computation_helper.mdFail',
      },
      TO_BE_RECTIFIED: {
        actuals: 0,
        displayName: 'bctx_computation_helper.toBeRectified',
      },
      REWORKED_FROM_DEFECTIVE: {
        actuals: 0,
        displayName: 'bctx_computation_helper.mdPassToBeRectified',
      },
      REJECTED_FROM_DEFECTIVE: {
        actuals: 0,
        displayName: 'bctx_computation_helper.mdFailToBeRectified',
      },
      DEFECTIVE: {
        actuals: 0,
        displayName: 'bctx_computation_helper.totalDefectivesAddedToday',
      },
    };
  },
  getUniqueBctxKeyWithOrder(bctxKeysOrder: TBCTXKeysWithDateTime[], bctxItemList: TBctxItem[]) {
    return bctxKeysOrder.reduce((acc, curr) => {
      let index = bctxItemList.findIndex((bctxItem) => bctxItem.k === curr);
      if (index > -1) acc = acc + '-' + bctxItemList[index].v;
      return acc;
    }, '');
  },
  getSizeFromBctxItems(bctxItems: TBctxItem[]) {
    const index = bctxItems.findIndex((bctxItem) => bctxItem.k === 'size');
    return bctxItems[index];
  },
  getValueByKeyInBctxItems(bctxItemListWithoutSize: TBctxItemWithoutSize[], key: TBCTXKeysWithoutSize) {
    const index = bctxItemListWithoutSize.findIndex((bctxItem) => bctxItem.k === key);
    return index > -1 && bctxItemListWithoutSize[index].v ? bctxItemListWithoutSize[index].v : null;
  },
  // product_type takes precedence; falls back to product_category for older bctx structures
  getProductTypeOrProductCategoryFromBctxItems(bctxItemListWithoutSize: TBctxItemWithoutSize[]) {
    return (
      BCTXComputationHelper.getValueByKeyInBctxItems(bctxItemListWithoutSize, 'product_type') ??
      BCTXComputationHelper.getValueByKeyInBctxItems(bctxItemListWithoutSize, 'product_category')
    );
  },
  filterBctxItemsWithKeys(bctxKeysOrder: TBCTXKeysWithoutSize[], bctxItems: TBctxItem[]): TBctxItemWithoutSize[] {
    return bctxKeysOrder.reduce((acc, curr) => {
      const bctxItem = bctxItems.find((bctxItem) => bctxItem.k === curr);
      if (bctxItem) {
        acc.push({
          k: curr,
          v: bctxItem.v,
        });
      }
      return acc;
    }, [] as TBctxItemWithoutSize[]);
  },
  getBctxContexts: (curr: TBctxItem[], bctxViz: BctxViz[]) => {
    return curr.length === 0
      ? []
      : curr.reduce((acc, { k, v }) => {
          const index = bctxViz.findIndex((viz) => viz.k === k);
          if (index > -1) {
            acc.push({ ...bctxViz[index], v });
          }
          return acc;
        }, [] as TCTXDTO[]);
  },
  getOrderedBctx: (bctx: TBctxItemWithoutSize[], order: TBCTXKeysWithoutSize[]) => {
    return order.reduce((acc, curr) => {
      const item = bctx.find((item) => item.k === curr);
      if (item) acc.push(item);
      return acc;
    }, [] as TBctxItemWithoutSize[]);
  },
  getOrderedBctxItems: (bctx: TBctxItem[], order: BctxViz[]) => {
    return order.reduce((acc, curr) => {
      const item = bctx.find((item) => item.k === curr.k);
      if (item) acc.push(item);
      return acc;
    }, [] as TBctxItem[]);
  },
  includeSizeAndOrderBctxItem: (bctx: TBctxItemWithoutSize[], bctxViz: BctxViz[], size?: string): TBctxItem[] => {
    const sizeBctxViz = bctxViz.find((viz) => viz.k === 'size');
    if (sizeBctxViz && sizeBctxViz.g && size) {
      return BCTXComputationHelper.getOrderedBctxItems([...bctx, { k: 'size', v: size }], bctxViz);
    } else return BCTXComputationHelper.getOrderedBctxItems(bctx, bctxViz);
  },
  getWhenObjectOfSaveEvent: (datetime: number) => {
    return {
      dt: datetime,
      tz: 'Asia/Colombo',
      os: new Date(datetime).getTimezoneOffset(),
    };
  },
  getMDPointDefectiveInfo: (gId?: string): TDefectiveInfo => {
    const defectives = [
      {
        operation: {
          category_id: '00',
          name: 'METAL DETECTED',
          group_name: '',
          display: true,
        },
        silhouette: {
          x: 0,
          y: 0,
          image: 'front',
        },
        issues: {
          category_id: '00',
          name: 'METAL DETECTED',
          id: '00',
        },
      },
    ];
    return {
      round: 1,
      id: gId ? gId : '',
      defectives,
      displayList: BCTXComputationHelper.getDefectivesDisplayList(defectives),
    };
  },

  getMdPointRejectReason: () => {
    const defectiveInfo = BCTXComputationHelper.getMDPointDefectiveInfo();
    return [defectiveInfo.defectives[0].issues as IRejectList];
  },

  getMDPointSingleEvent: (
    actionType: TMDPointGradingPageAction,
    eventCommonDetails: TEventCommonDetails,
    recentDefectMetaData?: TOperationIssueAndMarkedLocations[],
  ): TForwardSingleEventWithoutStatus => {
    switch (actionType) {
      case 'FTT':
        return {
          type: 'FORWARD_EVENT',
          actionType: 'FTT',
          eventInfo: eventCommonDetails,
        };
      case 'REJECTED':
        return {
          type: 'FORWARD_EVENT',
          actionType: 'REJECTED',
          eventInfo: eventCommonDetails,
          rejectedInfo: {
            reasons: BCTXComputationHelper.getMdPointRejectReason(),
          },
          imageId: null,
        };
      case 'IN_PROGRESS':
        return {
          type: 'FORWARD_EVENT',
          actionType: 'DEFECTIVE',
          eventInfo: eventCommonDetails,
          defectivesInfo: BCTXComputationHelper.getMDPointDefectiveInfo(),
          subType: 'DEFECTIVE',
          recentDefectMetaData: recentDefectMetaData ? recentDefectMetaData : [],
          sourceDefectiveCreatedTime: eventCommonDetails.eventTime,
          imageId: null,
        };
      case 'CHECK_IN':
        return {
          type: 'FORWARD_EVENT',
          actionType: 'CHECK_IN',
          eventInfo: eventCommonDetails,
        };
    }
  },
  getMDPointDefectiveResolutionEvent: (
    actionType: TDefectiveResolutionActionType,
    eventCommonDetails: TEventCommonDetails,
    sourceDefectiveCreatedTime: number,
    gId: string,
  ): TForwardSingleEventWithoutStatus => {
    const defectiveInfo = BCTXComputationHelper.getMDPointDefectiveInfo(gId);
    switch (actionType) {
      case 'REWORKED_FROM_DEFECTIVE':
        return {
          type: 'FORWARD_EVENT',
          actionType: 'REWORKED_FROM_DEFECTIVE',
          eventInfo: eventCommonDetails,
          defectivesInfo: defectiveInfo,
          sourceDefectiveCreatedTime: sourceDefectiveCreatedTime,
          imageId: null,
        };
      case 'REJECTED_FROM_DEFECTIVE':
        return {
          type: 'FORWARD_EVENT',
          actionType: 'REJECTED_FROM_DEFECTIVE',
          eventInfo: eventCommonDetails,
          defectivesInfo: defectiveInfo,
          sourceDefectiveCreatedTime: sourceDefectiveCreatedTime,
          rejectedInfo: {
            reasons: [defectiveInfo.defectives[0].issues as IRejectList],
          },
          imageId: null,
        };
    }
  },
  createInitialLineMapItem: (
    businessContextData: TRESPONSE_BusinessContextData,
    shiftId: string,
    bctxKeysOrder: TBCTXKeysWithoutSize[],
    selectedBctxItem: TSelectedBctxInfo,
    discardWipUpdates: boolean,
    bulkTodayAndPastDaysEventsWithBgStatus?: TPastDaysBackgroundEventsSyncInfo,
    delayWipUpdatesByMin?: number,
  ): TLineMapItem => {
    const bctxSyncTime = new Date().getTime();
    const lineMapCreationItems: TLineMapCreationItems[] = businessContextData.map((data) => {
      const bctxItemListWithoutSize = BCTXComputationHelper.filterBctxItemsWithKeys(bctxKeysOrder, data.bctx_items);
      const size = BCTXComputationHelper.getSizeFromBctxItems(data.bctx_items)?.v;
      const bctx_key = BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrder, bctxItemListWithoutSize);
      const wip_qty =
        !discardWipUpdates &&
        BCTXComputationHelper.checkIfWipUpdatesAllowed(
          bctxSyncTime,
          bctx_key,
          bulkTodayAndPastDaysEventsWithBgStatus,
          delayWipUpdatesByMin,
        ) &&
        (data.bctx_type === 'wip' || data.bctx_type === 'order')
          ? data.bctx_values.shippable_quantity
            ? data.bctx_values.shippable_quantity
            : data.wip_qty
          : 0;
      return { bctxItemListWithoutSize, size, bctx_key, wip_qty };
    });
    return BCTXComputationHelper.createInitialLineMapItemFromLineMapCreationItemList(
      lineMapCreationItems,
      shiftId,
      selectedBctxItem,
    );
  },
  getBctxMinMaxDateTime(businessContextData: TRESPONSE_BusinessContextData) {
    const bctxMinMaxDateTime: TBctxMinMaxDateTime = {
      minDate: null,
      maxDate: null,
    };

    businessContextData.forEach((bctxInfo) => {
      const currentDate =
        bctxInfo.bctx_type === 'plan' ? bctxInfo.bctx_datetime.key.datetime : bctxInfo.bctx_datetime.datetime;

      if (bctxMinMaxDateTime.minDate === null || currentDate < bctxMinMaxDateTime.minDate) {
        bctxMinMaxDateTime.minDate = currentDate;
      }

      if (bctxMinMaxDateTime.maxDate === null || currentDate > bctxMinMaxDateTime.maxDate) {
        bctxMinMaxDateTime.maxDate = currentDate;
      }
    });

    return bctxMinMaxDateTime;
  },
  updateCurrentDBWithWipMapAndShiftMapForTheNewBctxSizes: (
    currentDB: TUserActualsDB,
    selectedStationIdAndShiftId: TSelectedStationIdAndShiftId,
    newBctxForPOSplit: TNewBctxItemInPOSplit,
    newBctxSizes: string[],
  ): TUserActualsDB => {
    const newBctxKey = newBctxForPOSplit.bctx.bctxKey;
    let clientBctx: TClient_BCTX = {
      [newBctxKey]: {
        bctx_key: newBctxKey,
        sizeMap: {},
      },
    };
    const currentAvailableBctxWipMap =
      currentDB.dailyCache.lineMap[selectedStationIdAndShiftId.stationId]?.bctxWipMap[newBctxKey];

    let bctxWipMap: TBctxWipMap = {
      [newBctxKey]: {
        bctxKey: newBctxKey,
        bctxItemListWithoutSize: newBctxForPOSplit.bctx.list,
        sizeToWipMap: {},
        filteredSizesList: currentAvailableBctxWipMap?.filteredSizesList || [],
      },
    };
    const currentAvailableShiftMap =
      currentDB.dailyCache.lineMap[selectedStationIdAndShiftId.stationId]?.shiftMap[selectedStationIdAndShiftId.shiftId]
        .clientBctx[newBctxKey];
    console.log({ currentAvailableShiftMap });
    newBctxSizes.forEach((size) => {
      const sizeToWipMapItem: TSizeInfo = {
        name: size,
        wip_qty: 0,
      };
      const sizeToShiftMapItem: TSizeMapItem = {
        name: size,
        client_work: BCTXComputationHelper.createInitialClientWork(),
      };
      bctxWipMap = produce(bctxWipMap, (draft) => {
        draft[newBctxKey].sizeToWipMap[size] = currentAvailableBctxWipMap?.sizeToWipMap[size] || sizeToWipMapItem;
      });
      clientBctx = produce(clientBctx, (draft) => {
        draft[newBctxKey].sizeMap[size] = currentAvailableShiftMap?.sizeMap[size] || sizeToShiftMapItem;
      });
    });
    return produce(currentDB, (draft) => {
      const { stationId, shiftId } = selectedStationIdAndShiftId;

      draft.dailyCache.lineMap = draft.dailyCache.lineMap || {};
      draft.dailyCache.lineMap[stationId] = draft.dailyCache.lineMap[stationId] || {
        bctxWipMap: {},
        selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
        shiftMap: {
          [shiftId]: {
            clientBctx: {},
            rectifiedBCTXKeysInfo: {},
          },
        },
      };

      draft.dailyCache.lineMap[stationId].bctxWipMap[newBctxKey] = bctxWipMap[newBctxKey];
      draft.dailyCache.lineMap[stationId].shiftMap[shiftId] = draft.dailyCache.lineMap[stationId].shiftMap[shiftId] || {
        clientBctx: {},
        rectifiedBCTXKeysInfo: {},
      };

      draft.dailyCache.lineMap[stationId].shiftMap[shiftId].clientBctx[newBctxKey] = clientBctx[newBctxKey];
    });
  },
  createInitialSizeMap: (sizes: string[]) => {
    const sizeMap: TSizeMap = {};
    sizes.forEach((size) => {
      const sizeMapItem: TSizeMapItem = {
        name: size,
        client_work: BCTXComputationHelper.createInitialClientWork(),
      };
      sizeMap[size] = sizeMapItem;
    });
    return sizeMap;
  },
  createInitialShiftMapItem: (bctxWipMap: TBctxWipMap) => {
    const clientBctx: TClient_BCTX = {};
    Object.entries(bctxWipMap).forEach(([bctx_key, bctxWipMapItem]) => {
      clientBctx[bctx_key] = {
        bctx_key: bctx_key,
        sizeMap: BCTXComputationHelper.createInitialSizeMap(
          Object.entries(bctxWipMapItem.sizeToWipMap).map(([size]) => size),
        ),
      };
    });
    const shiftMapItem: TShiftMapItem = {
      clientBctx,
      rectifiedBCTXKeysInfo: {},
    };
    return shiftMapItem;
  },
  getRestoredClientBctxWithToBeRectifiedCounts: (
    orderKeys: TBCTXKeysWithoutSize[],
    currentClientBctx: TClient_BCTX,
    bctxItems: TRESPONSE_RestoreToBeRectifiedCounts['bctxItems'],
  ) => {
    bctxItems.forEach((bctxItem) => {
      const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(orderKeys, bctxItem.preSizeLevelBctx);
      //this check is needed when we get save event error for new bctx from defectives list(as defectives list is real time)
      //and these new bctx are not present in local-cache for updating counts
      if (currentClientBctx[bctxKey]) {
        Object.entries(bctxItem.sizeToCountMap).map(([size, countInfo]) => {
          //this check is needed when we get save event error for new bctx from defectives list(as defectives list is real time)
          //and these new bctx are not present in local-cache for updating counts
          if (currentClientBctx[bctxKey].sizeMap[size]) {
            currentClientBctx = produce(currentClientBctx, (draft) => {
              draft[bctxKey].sizeMap[size].client_work.IN_PROGRESS.actuals = countInfo.todaysToBeRectifiedCount;
              draft[bctxKey].sizeMap[size].client_work.TO_BE_RECTIFIED.actuals = countInfo.totalToBeRectifiedCount;
            });
          }
        });
      }
    });
    return currentClientBctx;
  },
  getBctxItemsFromBusinessContextListingItemAndSize(selectedBctx: TBctxItemWithoutSize[], size: string) {
    const arrayWithoutSize = selectedBctx.map(({ k, v }) => {
      return { k: k as TBctxItem['k'], v: v };
    });
    arrayWithoutSize.push({ k: 'size' as TBctxItem['k'], v: size });
    return arrayWithoutSize;
  },
  isPerPieceApp(app_identifier: TAppIdentifier | boolean) {
    return (
      app_identifier === 'md_point_per_piece_qc_v2' ||
      app_identifier === 'per_piece_line_in_v2' ||
      app_identifier === 'per_piece_qc_v2'
    );
  },
  getColumnsForUndo: (
    selectedBctx: TBctxItemWithoutSize[],
    size: string,
    app_identifier: TAppIdentifier | boolean,
  ): TBctxItem[] => {
    const ignoreKeys: TBCTXKeysWithoutSize[] = ['search_id', 'display_piece_tag', 'audit_disName', 'bundle_no'];
    return produce([] as TBctxItem[], (draft) => {
      selectedBctx.forEach((item) => {
        if (!BCTXComputationHelper.isPerPieceApp(app_identifier) || !ignoreKeys.includes(item.k)) draft.push(item);
      });
      draft.push({
        k: 'size',
        v: size,
      });
    });
  },
  getStateWithUpdatedToBeRectifiedKeysNew: (
    state: TUserActualsDB,
    subjectId: string,
    shiftId: string,
    rectifiedBCTXKeysInfo: TRectifiedBCTXInfo[],
  ): TUserActualsDB => {
    rectifiedBCTXKeysInfo.forEach((keyInfo) => {
      state = produce(state, (draft) => {
        draft.dailyCache.lineMap[subjectId].shiftMap[shiftId].rectifiedBCTXKeysInfo[keyInfo.key] = keyInfo;
      });
    });
    return state;
  },
  getUniqueVId() {
    return new Date().getTime() + '_' + Math.random().toString(36).substring(2); // TODO: use uuid to generate unique key
  },
  reconcileWipAndActualsFromPastdays(lineMap: TLineMap, incompleteBundlesLineMap?: TIncompleteBundlesInfoLineMap) {
    const updatedLineMap: TLineMap = {};
    Object.entries(lineMap).forEach(([line, lineMapItem]) => {
      const bctxWipMap = Object.entries(lineMapItem.bctxWipMap).reduce((wipAcc, [bctxKey, wipMapItem]) => {
        const sizeToWipMapItem = Object.entries(wipMapItem.sizeToWipMap).reduce((sizeAcc, [size, sizeInfo]) => {
          let isIncompleteBundle = false;
          if (
            incompleteBundlesLineMap &&
            incompleteBundlesLineMap[line] &&
            Object.values(incompleteBundlesLineMap[line]).length > 0
          ) {
            isIncompleteBundle = !!Object.values(incompleteBundlesLineMap[line])[0].list.find(
              ({ g_ctx_key }) => g_ctx_key === bctxKey,
            );
          }
          if (sizeInfo.wip_qty > 0 || isIncompleteBundle) sizeAcc[size] = sizeInfo;
          return sizeAcc;
        }, {} as TSizeToWipMapItem);
        if (Object.keys(sizeToWipMapItem).length > 0) {
          wipAcc[bctxKey] = {
            sizeToWipMap: sizeToWipMapItem,
            bctxKey: bctxKey,
            bctxItemListWithoutSize: wipMapItem.bctxItemListWithoutSize,
            filteredSizesList: wipMapItem.filteredSizesList,
          };
        }
        return wipAcc;
      }, {} as TBctxWipMap);
      if (Object.keys(bctxWipMap).length > 0) {
        updatedLineMap[line] = {
          bctxWipMap,
          shiftMap: Object.keys(lineMapItem.shiftMap).reduce((acc, shift) => {
            acc[shift] = BCTXComputationHelper.createInitialShiftMapItem(bctxWipMap);
            return acc;
          }, {} as TShiftMap),
          selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
        };
      }
    });
    return updatedLineMap;
  },

  getUpdatedClientWorkWithoutToBeRectifiedCounts: (
    client_work: TClientWork,
    bctxLevelSyncedState: TBctxLevelSyncedState,
  ): TClientWork => {
    return produce(client_work, (draft_client_work) => {
      draft_client_work['CHECK_IN'].actuals = bctxLevelSyncedState.actualsCount.checkin;
      draft_client_work['FTT'].actuals = bctxLevelSyncedState.actualsCount.ftt;
      draft_client_work['REJECTED'].actuals = bctxLevelSyncedState.actualsCount.reject;
      draft_client_work['REJECTED_FROM_DEFECTIVE'].actuals = bctxLevelSyncedState.actualsCount.reject_r;
      draft_client_work['REWORKED_FROM_DEFECTIVE'].actuals = bctxLevelSyncedState.actualsCount.rectified;
      draft_client_work['DEFECTIVE'].actuals = bctxLevelSyncedState.actualsCount.defectives;
    });
  },
  getUpdatedSizeMapWithoutToBeRectifiedCounts: (
    bctxItemAndSyncedStateSize: TBctxItemAndSyncedStateSize,
    sizeMap: TSizeMap,
  ): TSizeMap => {
    bctxItemAndSyncedStateSize.sizes.forEach((bctxLevelSyncedState) => {
      if (!sizeMap[bctxLevelSyncedState.size]) return;
      const updatedClientWork = BCTXComputationHelper.getUpdatedClientWorkWithoutToBeRectifiedCounts(
        sizeMap[bctxLevelSyncedState.size].client_work,
        bctxLevelSyncedState,
      );
      sizeMap = produce(sizeMap, (draft) => {
        draft[bctxLevelSyncedState.size].client_work = updatedClientWork;
      });
    });
    return sizeMap;
  },
  getFilteredBctxWithMultipleSizeList: (
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    currentDB: TUserActualsDB['dailyCache'],
    lineMapItem: TLineMapItem,
  ): TBctxWithMultipleSize[] => {
    const { stationId } = selectedStationAndShiftId;
    const bctxWithMultipleSizeList: TBctxWithMultipleSize[] = [];
    Object.entries(lineMapItem.bctxWipMap).forEach(([bctxKey, bctxWipMapItem]) => {
      let sizes = Object.entries(bctxWipMapItem.sizeToWipMap).map(([sizeName]) => sizeName);
      if (currentDB.lineMap[stationId] && currentDB.lineMap[stationId].bctxWipMap[bctxKey]) {
        sizes = sizes.filter((item) => !currentDB.lineMap[stationId].bctxWipMap[bctxKey].sizeToWipMap[item]);
      }
      if (sizes.length > 0)
        bctxWithMultipleSizeList.push({
          preSizeLevelBctx: bctxWipMapItem.bctxItemListWithoutSize,
          sizes,
        });
    });
    return bctxWithMultipleSizeList;
  },
  getBctxWithMultipleSizeList: (lineMapItem: TLineMapItem): TBctxWithMultipleSize[] => {
    const bctxWithMultipleSizeList: TBctxWithMultipleSize[] = [];
    Object.entries(lineMapItem.bctxWipMap).forEach(([, bctxWipMapItem]) => {
      const sizes = Object.entries(bctxWipMapItem.sizeToWipMap).map(([sizeName]) => sizeName);
      bctxWithMultipleSizeList.push({
        preSizeLevelBctx: bctxWipMapItem.bctxItemListWithoutSize,
        sizes,
      });
    });
    return bctxWithMultipleSizeList;
  },
  getUpdatedClientBctxWithMergedSizeMap: (currentClientBctx: TClient_BCTX, bctxKey: string, newSizeMap: TSizeMap) => {
    let currentSizeMap = currentClientBctx[bctxKey].sizeMap;
    let hasUpdates = false;
    Object.entries(newSizeMap).forEach(([size, sizeMapItem]) => {
      if (!currentSizeMap[size]) {
        hasUpdates = true;
        currentSizeMap = produce(currentSizeMap, (draft) => {
          draft[size] = sizeMapItem;
        });
      }
    });
    if (hasUpdates) {
      return produce(currentClientBctx, (draft) => {
        draft[bctxKey].sizeMap = currentSizeMap;
      });
    } else return currentClientBctx;
  },
  getMergedClientBctx: (newClientBctx: TClient_BCTX, currentClientBctx: TClient_BCTX) => {
    Object.entries(newClientBctx).forEach(([bctx_key, clientBctxItem]) => {
      if (currentClientBctx[bctx_key]) {
        currentClientBctx = BCTXComputationHelper.getUpdatedClientBctxWithMergedSizeMap(
          currentClientBctx,
          bctx_key,
          clientBctxItem.sizeMap,
        );
      } else {
        currentClientBctx = produce(currentClientBctx, (draft) => {
          draft[bctx_key] = clientBctxItem;
        });
      }
    });
    return currentClientBctx;
  },
  getMergedDBOnBctxLoad: (
    currentDB: TUserActualsDB['dailyCache'],
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    lineMapItem: TLineMapItem,
    bctxWithMultipleSizeList: TBctxWithMultipleSize[],
    bctxKeysOrder: TBCTXKeysWithoutSize[],
    bulkTodayAndPastDaysEventsWithBgStatus?: TPastDaysBackgroundEventsSyncInfo,
    delayWipUpdatesByMin?: number,
  ) => {
    const { stationId, shiftId } = selectedStationAndShiftId;
    const bctxSyncTime = new Date().getTime();
    if (!currentDB.lineMap[stationId]) {
      return produce(currentDB, (draft) => {
        draft.lineMap[stationId] = lineMapItem;
      });
    } else {
      //setting wip for all sizes to 0, if preSizeLevel bctx+size is not available from getbctx response
      currentDB = produce(currentDB, (draft) => {
        Object.entries(draft.lineMap[stationId].bctxWipMap).forEach(([bctxkey, { sizeToWipMap }]) => {
          Object.keys(sizeToWipMap).forEach((size) => {
            if (!lineMapItem.bctxWipMap[bctxkey] || !lineMapItem.bctxWipMap[bctxkey].sizeToWipMap[size]) {
              draft.lineMap[stationId].bctxWipMap[bctxkey].sizeToWipMap[size].wip_qty = 0;
            }
          });
        });
      });
      bctxWithMultipleSizeList.forEach((bctxWithMultipleSizeItem) => {
        const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          bctxKeysOrder,
          bctxWithMultipleSizeItem.preSizeLevelBctx,
        );
        if (currentDB.lineMap[stationId].bctxWipMap[bctxKey]) {
          Object.entries(lineMapItem.bctxWipMap[bctxKey].sizeToWipMap).forEach(([sizeName, sizeInfo]) => {
            // Get event available in local db

            if (!currentDB.lineMap[stationId].bctxWipMap[bctxKey].sizeToWipMap[sizeName]) {
              if (
                BCTXComputationHelper.checkIfWipUpdatesAllowed(
                  bctxSyncTime,
                  bctxKey,
                  bulkTodayAndPastDaysEventsWithBgStatus,
                  delayWipUpdatesByMin,
                )
              ) {
                currentDB = produce(currentDB, (draft) => {
                  draft.lineMap[stationId].bctxWipMap[bctxKey].sizeToWipMap[sizeName] = sizeInfo;
                });
              }
            } else if (
              sizeInfo.wip_qty !== currentDB.lineMap[stationId].bctxWipMap[bctxKey].sizeToWipMap[sizeName].wip_qty
            ) {
              if (
                BCTXComputationHelper.checkIfWipUpdatesAllowed(
                  bctxSyncTime,
                  bctxKey,
                  bulkTodayAndPastDaysEventsWithBgStatus,
                  delayWipUpdatesByMin,
                )
              ) {
                currentDB = produce(currentDB, (draft) => {
                  draft.lineMap[stationId].bctxWipMap[bctxKey].sizeToWipMap[sizeName].wip_qty = sizeInfo.wip_qty;
                });
              }
            }
          });
        } else {
          if (
            BCTXComputationHelper.checkIfWipUpdatesAllowed(
              bctxSyncTime,
              bctxKey,
              bulkTodayAndPastDaysEventsWithBgStatus,
              delayWipUpdatesByMin,
            )
          ) {
            currentDB = produce(currentDB, (draft) => {
              draft.lineMap[stationId].bctxWipMap[bctxKey] = lineMapItem.bctxWipMap[bctxKey];
            });
          }
        }
      });
      if (!currentDB.lineMap[stationId].shiftMap[shiftId]) {
        currentDB = produce(currentDB, (draft) => {
          draft.lineMap[stationId].shiftMap[shiftId] = lineMapItem.shiftMap[shiftId];
        });
      }
      Object.entries(currentDB.lineMap[stationId].shiftMap).forEach(([shift_id]) => {
        const updatedClientBctx = BCTXComputationHelper.getMergedClientBctx(
          lineMapItem.shiftMap[shiftId].clientBctx,
          currentDB.lineMap[stationId].shiftMap[shift_id].clientBctx,
        );
        currentDB = produce(currentDB, (draft) => {
          draft.lineMap[stationId].shiftMap[shift_id].clientBctx = updatedClientBctx;
        });
      });
      return currentDB;
    }
  },
  checkIfWipUpdatesAllowed: (
    bctxSyncTime: number,
    bctxKey: string,
    bulkTodayAndPastDaysEventsWithBgStatus?: TPastDaysBackgroundEventsSyncInfo,
    delayWipUpdatesByMin?: number,
  ): boolean => {
    return (
      !delayWipUpdatesByMin ||
      !bulkTodayAndPastDaysEventsWithBgStatus?.[bctxKey] ||
      bctxSyncTime - bulkTodayAndPastDaysEventsWithBgStatus[bctxKey].syncTime >= delayWipUpdatesByMin * 60 * 1000
    );
  },
  getUpdatedSizeToWipMap: (
    bctxItemAndSyncedStateSize: TBctxItemAndSyncedStateSize,
    sizeToWipMap: TSizeToWipMapItem,
  ): TSizeToWipMapItem => {
    return produce(sizeToWipMap, (draft_sizeToWipMap) => {
      bctxItemAndSyncedStateSize.sizes.forEach((bctxLevelSyncedState) => {
        if (draft_sizeToWipMap[bctxLevelSyncedState.size]) {
          draft_sizeToWipMap[bctxLevelSyncedState.size].wip_qty = bctxLevelSyncedState.remainingWipQty;
        }
      });
    });
  },
  getUpdatedSizeToWipMapBasedOnActuals: (
    bctxLevelTotalCheckins: TBctxLevelTotalCheckins | undefined,
    sizeToWipMap: TSizeToWipMapItem,
  ): TSizeToWipMapItem => {
    return produce(sizeToWipMap, (draft_sizeToWipMap) => {
      bctxLevelTotalCheckins &&
        bctxLevelTotalCheckins.totalCheckinsBySize.forEach((bctxLevelTotalCheckin) => {
          if (!draft_sizeToWipMap[bctxLevelTotalCheckin.size]) return;
          draft_sizeToWipMap[bctxLevelTotalCheckin.size].wip_qty =
            draft_sizeToWipMap[bctxLevelTotalCheckin.size].wip_qty - bctxLevelTotalCheckin.checkin;
        });
    });
  },
  getMergedDBOnRestore: (
    orderKeys: TBCTXKeysWithoutSize[],
    wip_only: boolean,
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    currentDB: TUserActualsDB['dailyCache'],
    appStateResponse: TRESPONSE_AppStateResponse,
  ) => {
    const { stationId, shiftId } = selectedStationAndShiftId;
    const bctxItemAndSyncedStateSizeList: TBctxItemAndSyncedStateSize[] = appStateResponse.appSyncedState
      .syncedStateBySubject[stationId]
      ? appStateResponse.appSyncedState.syncedStateBySubject[stationId].bctxLevelSyncedStates
      : [];
    bctxItemAndSyncedStateSizeList.forEach((bctxItemAndSyncedStateSizeItem) => {
      const bctx_key = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
        orderKeys,
        bctxItemAndSyncedStateSizeItem.preSizeLevelBctx,
      );
      bctxItemAndSyncedStateSizeItem.sizes.forEach((size) => {
        const updatedLineMap = BCTXComputationHelper.backFillBctxInUserLineMap(
          {
            bctxKey: bctx_key,
            businessContextDetailsWithoutSize: BCTXComputationHelper.getOrderedBctx(
              bctxItemAndSyncedStateSizeItem.preSizeLevelBctx,
              orderKeys,
            ),
            size: size.size,
            count: 0,
          },
          currentDB.lineMap,
          stationId,
          shiftId,
        );
        currentDB = produce(currentDB, (draft) => {
          draft.lineMap = updatedLineMap;
        });
      });
      const updatedSizeToWipMap = BCTXComputationHelper.getUpdatedSizeToWipMap(
        bctxItemAndSyncedStateSizeItem,
        currentDB.lineMap[stationId].bctxWipMap[bctx_key].sizeToWipMap,
      );
      currentDB = produce(currentDB, (draft) => {
        draft.lineMap[stationId].bctxWipMap[bctx_key].sizeToWipMap = updatedSizeToWipMap;
      });
      if (!wip_only) {
        const updatedSizeMapWithoutToBeRectifiedCounts =
          BCTXComputationHelper.getUpdatedSizeMapWithoutToBeRectifiedCounts(
            bctxItemAndSyncedStateSizeItem,
            currentDB.lineMap[stationId].shiftMap[shiftId].clientBctx[bctx_key].sizeMap,
          );
        currentDB = produce(currentDB, (draft) => {
          draft.lineMap[stationId].shiftMap[shiftId].clientBctx[bctx_key].sizeMap =
            updatedSizeMapWithoutToBeRectifiedCounts;
        });
      }
    });
    return currentDB;
  },

  backFillBackedUpBctxIntoClientDBIfNotPresent(
    clientDB: TUserActualsDB['dailyCache'],
    bctxItemAndSyncedStateSizeList: TBctxWithMultipleSize[],
    orderKeys: TBCTXKeysWithoutSize[],
    stationId: string,
    shiftId: string,
    app_identifier: TAppIdentifier | boolean,
  ) {
    clientDB = produce(clientDB, (draft_client_db) => {
      if (!draft_client_db.lineMap[stationId]) {
        draft_client_db.lineMap[stationId] = {
          bctxWipMap: {},
          selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
          shiftMap: {
            [shiftId]: {
              clientBctx: {},
              rectifiedBCTXKeysInfo: {},
            },
          },
        };
      } else if (!draft_client_db.lineMap[stationId].shiftMap[shiftId]) {
        draft_client_db.lineMap[stationId].shiftMap[shiftId] = {
          clientBctx: {},
          rectifiedBCTXKeysInfo: {},
        };
      }
    });
    bctxItemAndSyncedStateSizeList.forEach(({ sizes, preSizeLevelBctx }) => {
      const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(orderKeys, preSizeLevelBctx);
      if (
        !clientDB.lineMap[stationId].bctxWipMap[bctxKey] ||
        sizes.find((size) => !clientDB.lineMap[stationId].bctxWipMap[bctxKey].sizeToWipMap[size])
      ) {
        const updatedWipMap = produce(clientDB.lineMap[stationId].bctxWipMap, (draft_wip_map) => {
          if (!draft_wip_map[bctxKey]) {
            draft_wip_map[bctxKey] = {
              bctxItemListWithoutSize: preSizeLevelBctx,
              bctxKey,
              sizeToWipMap: sizes.reduce((acc, size) => {
                acc[size] = {
                  name: size,
                  wip_qty: 0,
                };
                return acc;
              }, {} as TSizeToWipMapItem),
              filteredSizesList: [],
            };
          } else if (app_identifier !== 'line_in_v2') {
            sizes.forEach((size) => {
              if (!draft_wip_map[bctxKey].sizeToWipMap[size]) {
                draft_wip_map[bctxKey].sizeToWipMap[size] = {
                  name: size,
                  wip_qty: 0,
                };
              }
            });
          }
        });
        clientDB = produce(clientDB, (draft) => {
          draft.lineMap[stationId].bctxWipMap = updatedWipMap;
        });
      }

      Object.keys(clientDB.lineMap[stationId].shiftMap).forEach((shiftId) => {
        if (
          !clientDB.lineMap[stationId].shiftMap[shiftId].clientBctx[bctxKey] ||
          sizes.find((size) => !clientDB.lineMap[stationId].shiftMap[shiftId].clientBctx[bctxKey].sizeMap[size])
        ) {
          const updatedShiftMap = produce(clientDB.lineMap[stationId].shiftMap, (draft_shift_map) => {
            if (!draft_shift_map[shiftId].clientBctx[bctxKey]) {
              draft_shift_map[shiftId].clientBctx[bctxKey] = {
                bctx_key: bctxKey,
                sizeMap: sizes.reduce((acc, size) => {
                  acc[size] = {
                    name: size,
                    client_work: BCTXComputationHelper.createInitialClientWork(),
                  };
                  return acc;
                }, {} as TSizeMap),
              };
            } else if (app_identifier !== 'line_in_v2') {
              sizes.forEach((size) => {
                if (!draft_shift_map[shiftId].clientBctx[bctxKey].sizeMap[size]) {
                  draft_shift_map[shiftId].clientBctx[bctxKey].sizeMap[size] = {
                    name: size,
                    client_work: BCTXComputationHelper.createInitialClientWork(),
                  };
                }
              });
            }
          });
          clientDB = produce(clientDB, (draft) => {
            draft.lineMap[stationId].shiftMap = updatedShiftMap;
          });
        }
      });
    });
    return clientDB;
  },

  getMergedDBOnRestoreForBackedUpBctx: (
    dto: TRestoreBackedUpBctx,
    orderKeys: TBCTXKeysWithoutSize[],
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    currentDB: TUserActualsDB['dailyCache'],
    appStateResponse: TRESPONSE_AppStateResponseForBackedUpBctx,
    isPendingWipBasedOnActuals: boolean,
    app_identifier: TAppIdentifier | boolean,
  ) => {
    appStateResponse.agentToShiftWiseBctx.forEach((agentToShiftWiseBctx) => {
      currentDB = BCTXComputationHelper.backFillBackedUpBctxIntoClientDBIfNotPresent(
        currentDB,
        agentToShiftWiseBctx.shiftWiseBctxs[0].bctx,
        orderKeys,
        agentToShiftWiseBctx.agent.subject,
        agentToShiftWiseBctx.shiftWiseBctxs[0].shiftId,
        app_identifier,
      );
    });
    const { stationId, shiftId } = selectedStationAndShiftId;
    const bctxItemAndSyncedStateSizeList: TBctxItemAndSyncedStateSize[] =
      appStateResponse.appSyncedState && appStateResponse.appSyncedState.bctxLevelSyncedStates
        ? appStateResponse.appSyncedState.bctxLevelSyncedStates
        : [];
    //using bctxKeyToUpdatedDetails, instead of updating directly in currentDB as with lot of entries,
    //updates to currentDB is taking time
    let bctxKeyToUpdatedDetails: Record<string, { sizeMap?: TSizeMap; sizeToWipMapItem?: TSizeToWipMapItem }> = {};

    if (app_identifier === 'line_in_v2' && isPendingWipBasedOnActuals) {
      const bctxLevelTotalCheckinsData = appStateResponse.bctxLevelTotalCheckins || [];
      bctxLevelTotalCheckinsData.forEach((bctxLevelTotalCheckins) => {
        const bctx_key = '-' + bctxLevelTotalCheckins.bctx;

        const updatedSizeToWipMap = BCTXComputationHelper.getUpdatedSizeToWipMapBasedOnActuals(
          bctxLevelTotalCheckins,
          currentDB.lineMap[stationId].bctxWipMap[bctx_key].sizeToWipMap,
        );

        bctxKeyToUpdatedDetails = {
          ...bctxKeyToUpdatedDetails,
          [bctx_key]: {
            ...(bctxKeyToUpdatedDetails[bctx_key] ? bctxKeyToUpdatedDetails[bctx_key] : {}),
            sizeToWipMapItem: updatedSizeToWipMap,
          },
        };
      });

      bctxItemAndSyncedStateSizeList.forEach((bctxItemAndSyncedStateSizeItem) => {
        const bctx_key = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          orderKeys,
          bctxItemAndSyncedStateSizeItem.preSizeLevelBctx,
        );

        const updatedSizeMapWithoutToBeRectifiedCounts =
          BCTXComputationHelper.getUpdatedSizeMapWithoutToBeRectifiedCounts(
            bctxItemAndSyncedStateSizeItem,
            currentDB.lineMap[stationId].shiftMap[shiftId].clientBctx[bctx_key].sizeMap,
          );
        bctxKeyToUpdatedDetails = {
          ...bctxKeyToUpdatedDetails,
          [bctx_key]: {
            ...(bctxKeyToUpdatedDetails[bctx_key] ? bctxKeyToUpdatedDetails[bctx_key] : {}),
            sizeMap: updatedSizeMapWithoutToBeRectifiedCounts,
          },
        };
      });
    } else {
      bctxItemAndSyncedStateSizeList.forEach((bctxItemAndSyncedStateSizeItem) => {
        const bctx_key = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          orderKeys,
          bctxItemAndSyncedStateSizeItem.preSizeLevelBctx,
        );
        if (!dto.actualsOnly) {
          const updatedSizeToWipMap = BCTXComputationHelper.getUpdatedSizeToWipMap(
            bctxItemAndSyncedStateSizeItem,
            currentDB.lineMap[stationId].bctxWipMap[bctx_key].sizeToWipMap,
          );

          bctxKeyToUpdatedDetails = {
            ...bctxKeyToUpdatedDetails,
            [bctx_key]: {
              ...(bctxKeyToUpdatedDetails[bctx_key] ? bctxKeyToUpdatedDetails[bctx_key] : {}),
              sizeToWipMapItem: updatedSizeToWipMap,
            },
          };
        }
        if (!dto.wipOnly) {
          const updatedSizeMapWithoutToBeRectifiedCounts =
            BCTXComputationHelper.getUpdatedSizeMapWithoutToBeRectifiedCounts(
              bctxItemAndSyncedStateSizeItem,
              currentDB.lineMap[stationId].shiftMap[shiftId].clientBctx[bctx_key].sizeMap,
            );
          bctxKeyToUpdatedDetails = {
            ...bctxKeyToUpdatedDetails,
            [bctx_key]: {
              ...(bctxKeyToUpdatedDetails[bctx_key] ? bctxKeyToUpdatedDetails[bctx_key] : {}),
              sizeMap: updatedSizeMapWithoutToBeRectifiedCounts,
            },
          };
        }
      });
    }
    currentDB = produce(currentDB, (draft) => {
      Object.entries(bctxKeyToUpdatedDetails).forEach(([bctxKey, data]) => {
        if (data.sizeToWipMapItem) draft.lineMap[stationId].bctxWipMap[bctxKey].sizeToWipMap = data.sizeToWipMapItem;
        if (data.sizeMap) draft.lineMap[stationId].shiftMap[shiftId].clientBctx[bctxKey].sizeMap = data.sizeMap;
      });
    });
    return currentDB;
  },

  backFillBctxInBgLineMap(
    bctxDetails: TEventBctxDetails,
    bgLineMap: TBgLineMap,
    stationDetails: TEventStationDetails,
    modifiedBctxKey?: string,
  ) {
    const bctxKey = modifiedBctxKey ? modifiedBctxKey : bctxDetails.bctxKey;
    const subject = stationDetails.subject.subject;
    const shift = stationDetails.shift.id;

    if (
      bgLineMap[subject] &&
      bgLineMap[subject].shiftMap[shift] &&
      bgLineMap[subject].bctxWipMap[bctxKey] &&
      bgLineMap[subject].bctxWipMap[bctxKey].sizeToWipMap[bctxDetails.size] &&
      bgLineMap[subject].shiftMap[shift].clientBctx[bctxKey] &&
      bgLineMap[subject].shiftMap[shift].clientBctx[bctxKey].sizeMap[bctxDetails.size]
    ) {
      return bgLineMap;
    }

    bgLineMap = produce(bgLineMap, (draft) => {
      if (!draft[subject]) {
        draft[subject] = {
          shiftMap: {
            [shift]: {
              clientBctx: {},
            },
          },
          bctxWipMap: {},
        };
      } else if (!draft[subject].shiftMap[shift]) {
        draft[subject].shiftMap[shift] = {
          clientBctx: {},
        };
      }
    });
    return produce(bgLineMap, (draft) => {
      if (!draft[subject].bctxWipMap[bctxKey]) {
        draft[subject].bctxWipMap[bctxKey] = {
          bctxItemListWithoutSize: bctxDetails.businessContextDetailsWithoutSize,
          bctxKey: bctxKey,
          sizeToWipMap: {
            [bctxDetails.size]: {
              name: bctxDetails.size,
              wip_qty: 0,
            },
          },
          filteredSizesList: [],
        };
      } else if (!draft[subject].bctxWipMap[bctxKey].sizeToWipMap[bctxDetails.size]) {
        draft[subject].bctxWipMap[bctxKey].sizeToWipMap[bctxDetails.size] = {
          name: bctxDetails.size,
          wip_qty: 0,
        };
      }
      if (!draft[subject].shiftMap[shift].clientBctx[bctxKey]) {
        draft[subject].shiftMap[shift].clientBctx[bctxKey] = {
          bctx_key: bctxKey,
          sizeMap: {
            [bctxDetails.size]: {
              name: bctxDetails.size,
              client_work: BCTXComputationHelper.createInitialClientWork(),
            },
          },
        };
      } else if (!draft[subject].shiftMap[shift].clientBctx[bctxKey].sizeMap[bctxDetails.size]) {
        draft[subject].shiftMap[shift].clientBctx[bctxKey].sizeMap[bctxDetails.size] = {
          name: bctxDetails.size,
          client_work: BCTXComputationHelper.createInitialClientWork(),
        };
      }
    });
  },

  backFillBctxInUserLineMap(
    bctxDetails: TEventBctxDetails,
    userLineMap: TLineMap,
    subject: string,
    shift: string,
    modifiedBctxKey?: string,
  ) {
    const bctxKey = modifiedBctxKey ? modifiedBctxKey : bctxDetails.bctxKey;
    if (
      userLineMap[subject] &&
      userLineMap[subject].shiftMap[shift] &&
      userLineMap[subject].bctxWipMap[bctxKey] &&
      userLineMap[subject].bctxWipMap[bctxKey].sizeToWipMap[bctxDetails.size]
    ) {
      let allShiftsComplete = true;
      Object.keys(userLineMap[subject].shiftMap).forEach((shiftKey) => {
        const clientBctxExists = userLineMap[subject].shiftMap[shiftKey].clientBctx[bctxKey];
        const sizeMapExists = clientBctxExists && clientBctxExists.sizeMap[bctxDetails.size];
        if (!clientBctxExists || !sizeMapExists) {
          allShiftsComplete = false;
        }
      });
      if (allShiftsComplete) {
        return userLineMap;
      }
    }

    userLineMap = produce(userLineMap, (draft) => {
      if (!draft[subject]) {
        draft[subject] = {
          shiftMap: {
            [shift]: {
              clientBctx: {},
              rectifiedBCTXKeysInfo: {},
            },
          },
          bctxWipMap: {},
          selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
        };
      } else if (!draft[subject].shiftMap[shift]) {
        draft[subject].shiftMap[shift] = {
          clientBctx: {},
          rectifiedBCTXKeysInfo: {},
        };
      }
    });
    userLineMap = produce(userLineMap, (draft) => {
      if (!draft[subject].bctxWipMap[bctxKey]) {
        draft[subject].bctxWipMap[bctxKey] = {
          bctxItemListWithoutSize: bctxDetails.businessContextDetailsWithoutSize,
          bctxKey: bctxKey,
          sizeToWipMap: {
            [bctxDetails.size]: {
              name: bctxDetails.size,
              wip_qty: 0,
            },
          },
          filteredSizesList: [],
        };
      } else if (!draft[subject].bctxWipMap[bctxKey].sizeToWipMap[bctxDetails.size]) {
        draft[subject].bctxWipMap[bctxKey].sizeToWipMap[bctxDetails.size] = {
          name: bctxDetails.size,
          wip_qty: 0,
        };
      }
    });
    Object.keys(userLineMap[subject].shiftMap).forEach((shiftKey) => {
      if (!userLineMap[subject].shiftMap[shiftKey].clientBctx[bctxKey]) {
        const updatedShiftMap = produce(userLineMap[subject].shiftMap, (draft_shift_map) => {
          draft_shift_map[shiftKey].clientBctx[bctxKey] = {
            bctx_key: bctxKey,
            sizeMap: {
              [bctxDetails.size]: {
                name: bctxDetails.size,
                client_work: BCTXComputationHelper.createInitialClientWork(),
              },
            },
          };
        });
        userLineMap = produce(userLineMap, (draft) => {
          draft[subject].shiftMap = updatedShiftMap;
        });
      } else if (!userLineMap[subject].shiftMap[shiftKey].clientBctx[bctxKey].sizeMap[bctxDetails.size]) {
        userLineMap = produce(userLineMap, (draft) => {
          draft[subject].shiftMap[shiftKey].clientBctx[bctxKey].sizeMap[bctxDetails.size] = {
            name: bctxDetails.size,
            client_work: BCTXComputationHelper.createInitialClientWork(),
          };
        });
      }
    });
    return userLineMap;
  },

  updateWipAndActualsForFailedBulkEventsWithFilteredSingleEvents(
    bgDB: TBackgroundEventsSyncingDB,
    failedBulkEvents: TBulkEvent[],
    filteredSingleEventStatus: TSingleCountEvent['eventBackgroundMetadata']['status'][],
    actualsOnly: boolean,
    orderKeys?: TBCTXKeysWithoutSize[],
  ) {
    failedBulkEvents.forEach((bulkEvent) => {
      if (bulkEvent.type === 'FORWARD_BULK_EVENT' || bulkEvent.type === 'UNDO_BULK_EVENT') {
        const stationDetails = bulkEvent.metadata.stationDetails;
        const direction = -1;
        bulkEvent.events.forEach((event) => {
          if (filteredSingleEventStatus.find((status) => status === event.eventBackgroundMetadata.status)) {
            const bctxDetails = event.eventInfo.bctxDetails;
            const modifiedBctxKey = orderKeys
              ? BCTXComputationHelper.getUniqueBctxKeyWithOrder(
                  orderKeys,
                  bctxDetails.businessContextDetailsWithoutSize,
                )
              : bctxDetails.bctxKey;
            const bgLineMap = BCTXComputationHelper.backFillBctxInBgLineMap(
              bctxDetails,
              bgDB.dailyCache.lineMap,
              stationDetails,
              orderKeys ? modifiedBctxKey : undefined,
            );
            bgDB = produce(bgDB, (currentDB) => {
              currentDB.dailyCache.lineMap = bgLineMap;
            });
            bgDB = produce(bgDB, (currentDB) => {
              const shiftMap = currentDB.dailyCache.lineMap[stationDetails.subject.subject].shiftMap;
              const wipMap = currentDB.dailyCache.lineMap[stationDetails.subject.subject].bctxWipMap;
              const client_work =
                shiftMap[stationDetails.shift.id].clientBctx[modifiedBctxKey].sizeMap[bctxDetails.size].client_work;
              const wipInfo = wipMap[modifiedBctxKey].sizeToWipMap[bctxDetails.size];
              switch (event.actionType) {
                case 'DEFECTIVE':
                case 'UNDO_DEFECTIVE':
                  if (event.subType === 'DEFECTIVE' || event.subType === 'UNDO_DEFECTIVE') {
                    if (!actualsOnly) {
                      wipInfo.wip_qty -=
                        bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    }
                    client_work.DEFECTIVE.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    client_work.IN_PROGRESS.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    client_work.TO_BE_RECTIFIED.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    return;
                  } else return;
                case 'CHECK_IN':
                case 'UNDO_CHECK_IN':
                  if (!actualsOnly) {
                    wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_CHECK_IN' ? -1 : 1) * direction;
                  }
                  client_work.CHECK_IN.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_CHECK_IN' ? -1 : 1) * direction;
                  return;
                case 'FTT':
                case 'UNDO_FTT':
                  if (!actualsOnly) {
                    wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                  }
                  client_work.FTT.actuals += bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                  return;
                case 'REJECTED':
                case 'UNDO_REJECTED':
                  if (!actualsOnly) {
                    wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                  }
                  client_work.REJECTED.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                  return;
                case 'REWORKED_FROM_DEFECTIVE':
                case 'UNDO_REWORKED_FROM_DEFECTIVE': {
                  const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                  client_work.REWORKED_FROM_DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  if (isToday) {
                    client_work.IN_PROGRESS.actuals -=
                      bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  }
                  return;
                }
                case 'REJECTED_FROM_DEFECTIVE':
                case 'UNDO_REJECTED_FROM_DEFECTIVE': {
                  const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                  client_work.REJECTED_FROM_DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  if (isToday) {
                    client_work.IN_PROGRESS.actuals -=
                      bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  }
                  return;
                }
              }
            });
          }
        });
      }
    });
    return bgDB;
  },

  updateWipAndActualsForBulkEventsForOnlineApps(sourceCurrentDB: TUserActualsDB, bulkEvents: TBulkEvent[]) {
    bulkEvents.forEach((bulkEvent) => {
      if (bulkEvent.type === 'FORWARD_BULK_EVENT' || bulkEvent.type === 'UNDO_BULK_EVENT') {
        const stationDetails = bulkEvent.metadata.stationDetails;
        const direction = 1;
        bulkEvent.events.forEach((event) => {
          if (event.eventBackgroundMetadata.status === 'SYNCED_WITH_SUCCESS') {
            const bctxDetails = event.eventInfo.bctxDetails;
            const updatedLineMap = BCTXComputationHelper.backFillBctxInUserLineMap(
              bctxDetails,
              sourceCurrentDB.dailyCache.lineMap,
              stationDetails.subject.subject,
              stationDetails.shift.id,
            );
            const currentBctxKey = bctxDetails.bctxKey;
            const newBctxKey = bctxDetails.newBctxDetails?.bctxKey;
            const bctx_key = newBctxKey ? newBctxKey : currentBctxKey;
            sourceCurrentDB = produce(sourceCurrentDB, (draft) => {
              draft.dailyCache.lineMap = updatedLineMap;
            });
            sourceCurrentDB = produce(sourceCurrentDB, (draft) => {
              const shiftMap = draft.dailyCache.lineMap[stationDetails.subject.subject].shiftMap;
              const wipMap = draft.dailyCache.lineMap[stationDetails.subject.subject].bctxWipMap;
              const client_work =
                shiftMap[stationDetails.shift.id].clientBctx[bctx_key].sizeMap[bctxDetails.size].client_work;
              const wipInfo = wipMap[currentBctxKey].sizeToWipMap[bctxDetails.size];
              switch (event.actionType) {
                case 'DEFECTIVE':
                case 'UNDO_DEFECTIVE':
                  if (event.subType === 'DEFECTIVE' || event.subType === 'UNDO_DEFECTIVE') {
                    wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    client_work.DEFECTIVE.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    client_work.IN_PROGRESS.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    client_work.TO_BE_RECTIFIED.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    return;
                  } else return;
                case 'FTT':
                case 'UNDO_FTT':
                  wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                  client_work.FTT.actuals += bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                  return;
                case 'REJECTED':
                case 'UNDO_REJECTED':
                  wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                  client_work.REJECTED.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                  return;
                case 'REWORKED_FROM_DEFECTIVE':
                case 'UNDO_REWORKED_FROM_DEFECTIVE': {
                  const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                  client_work.REWORKED_FROM_DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  if (isToday) {
                    client_work.IN_PROGRESS.actuals -=
                      bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  }
                  return;
                }
                case 'REJECTED_FROM_DEFECTIVE':
                case 'UNDO_REJECTED_FROM_DEFECTIVE': {
                  const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                  client_work.REJECTED_FROM_DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  if (isToday) {
                    client_work.IN_PROGRESS.actuals -=
                      bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  }
                  return;
                }
              }
            });
          }
        });
      }
    });
    return sourceCurrentDB;
  },

  updateWipAndActualsForBulkEventsForAllSingleEvents(
    currentDB: TUserActualsDB,
    bulkEvents: TBulkEventWithoutStatus[],
    updateActualsOnly: boolean,
    orderKeys?: TBCTXKeysWithoutSize[],
  ) {
    bulkEvents.forEach((bulkEvent) => {
      if (bulkEvent.type === 'FORWARD_BULK_EVENT' || bulkEvent.type === 'UNDO_BULK_EVENT') {
        const stationDetails = bulkEvent.metadata.stationDetails;
        const direction = 1;
        bulkEvent.events.forEach((event) => {
          const bctxDetails = event.eventInfo.bctxDetails;

          const modifiedBctxKey = orderKeys
            ? BCTXComputationHelper.getUniqueBctxKeyWithOrder(orderKeys, bctxDetails.businessContextDetailsWithoutSize)
            : bctxDetails.bctxKey;
          const updatedLineMap = BCTXComputationHelper.backFillBctxInUserLineMap(
            bctxDetails,
            currentDB.dailyCache.lineMap,
            stationDetails.subject.subject,
            stationDetails.shift.id,
            orderKeys ? modifiedBctxKey : undefined,
          );
          currentDB = produce(currentDB, (draft) => {
            draft.dailyCache.lineMap = updatedLineMap;
          });
          currentDB = produce(currentDB, (draft) => {
            const shiftMap = draft.dailyCache.lineMap[stationDetails.subject.subject].shiftMap;
            const wipMap = draft.dailyCache.lineMap[stationDetails.subject.subject].bctxWipMap;
            const client_work =
              shiftMap[stationDetails.shift.id].clientBctx[modifiedBctxKey].sizeMap[bctxDetails.size].client_work;
            const wipInfo = wipMap[modifiedBctxKey].sizeToWipMap[bctxDetails.size];
            switch (event.actionType) {
              case 'DEFECTIVE':
              case 'UNDO_DEFECTIVE':
                if (event.subType === 'DEFECTIVE' || event.subType === 'UNDO_DEFECTIVE') {
                  if (!updateActualsOnly) {
                    wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                  }
                  client_work.DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.IN_PROGRESS.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                  return;
                } else return;
              case 'CHECK_IN':
              case 'UNDO_CHECK_IN':
                if (!updateActualsOnly) {
                  wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_CHECK_IN' ? -1 : 1) * direction;
                }
                // Clamp at 0: an undo can be validated against the server's aggregate remaining
                // quantity while local actuals is still stale/desynced, which would otherwise drive
                // this negative before the next restore/reconcile corrects it.
                client_work.CHECK_IN.actuals = Math.max(
                  0,
                  client_work.CHECK_IN.actuals +
                    bctxDetails.count * (event.actionType === 'UNDO_CHECK_IN' ? -1 : 1) * direction,
                );
                return;
              case 'FTT':
              case 'UNDO_FTT':
                if (!updateActualsOnly) {
                  wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                }
                client_work.FTT.actuals += bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                return;
              case 'REJECTED':
              case 'UNDO_REJECTED':
                if (!updateActualsOnly) {
                  wipInfo.wip_qty -= bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                }
                client_work.REJECTED.actuals +=
                  bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                return;
              case 'REWORKED_FROM_DEFECTIVE':
              case 'UNDO_REWORKED_FROM_DEFECTIVE': {
                const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                client_work.REWORKED_FROM_DEFECTIVE.actuals +=
                  bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                client_work.TO_BE_RECTIFIED.actuals -=
                  bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                if (isToday) {
                  client_work.IN_PROGRESS.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                }
                return;
              }
              case 'REJECTED_FROM_DEFECTIVE':
              case 'UNDO_REJECTED_FROM_DEFECTIVE': {
                const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                client_work.REJECTED_FROM_DEFECTIVE.actuals +=
                  bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                client_work.TO_BE_RECTIFIED.actuals -=
                  bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                if (isToday) {
                  client_work.IN_PROGRESS.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                }
                return;
              }
            }
          });
        });
      }
    });
    return currentDB;
  },

  releaseUserDBLock(draft: TUserActualsDBLockDetails) {
    return produce(draft, (currentDB) => {
      currentDB.isLocked = false;
      currentDB.lockedBy = 'NONE';
    });
  },

  releaseBgLock(draft: TBgEventsSyncingDBLockDetails, lockUpTo?: number) {
    return produce(draft, (currentDB) => {
      if (currentDB.lockedBy === 'SYNC_EVENTS') {
        if (lockUpTo) currentDB.metadata['SYNC_EVENTS'].nextLockUpto = lockUpTo;
        else currentDB.metadata['SYNC_EVENTS'] = {};
      }
      currentDB.isLocked = false;
      currentDB.lockedBy = 'NONE';
    });
  },

  transformToServerDefectives(
    response: TRESPONSE_ServerDefectives,
    shiftId: string,
    bctxKeysOrderWithoutSize: TBCTXKeysWithoutSize[],
  ): TServerDefectivesDB['dailyCache']['lineWiseServerDefectives'] {
    let lineWiseServerDefectives = {} as TServerDefectivesDB['dailyCache']['lineWiseServerDefectives'];
    Object.entries(response.data).forEach(([subject, defectivesList]) => {
      lineWiseServerDefectives[subject] = {
        shiftWiseServerDefectives: {
          [shiftId]: defectivesList.map((defective, index) => {
            const bctxItem = defective.ctx.map((ctx) => {
              return {
                k: ctx.k,
                v: ctx.v,
              };
            });
            const dT = defective.id.split('@');
            const defectList = defective.event_ac.map((eventActual) => {
              return {
                issues: eventActual.issues as unknown as TDefectiveOperationIssueAndSilhouetteMarkingInfo['issues'],
                operation:
                  eventActual.operation as unknown as TDefectiveOperationIssueAndSilhouetteMarkingInfo['operation'],
                silhouette:
                  eventActual.silhouette as unknown as TDefectiveOperationIssueAndSilhouetteMarkingInfo['silhouette'],
              };
            });
            let datetime = defective.datetime ? parseInt(defective.datetime) : parseInt(dT[dT.length - 1]);
            return {
              actionType: 'SERVER_DEFECTIVE',
              eventInfo: {
                eventTime: datetime,
                bctxDetails: {
                  businessContextDetailsWithoutSize: bctxItem,
                  bctxKey: BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrderWithoutSize, bctxItem),
                  size: defective.size,
                  count: 1,
                },
                line: subject,
                shift: shiftId,
              },
              defectivesInfo: {
                round: defective.round,
                id: defective.id,
                defectives: defectList,
                displayList: this.getDefectivesDisplayList(defectList),
              },
              sourceDefectiveCreatedTime: parseInt(dT[dT.length - 1]),
              imageId: BCTXComputationHelper.isTodayOrYesterday(datetime) ? defective.defective_image_id : null,
            };
          }),
        },
      };
    });
    return lineWiseServerDefectives;
  },

  getDefectivesDisplayList(eventActual: TDefectiveOperationIssueAndSilhouetteMarkingInfo[]) {
    const operationToIssuesListMap = eventActual.reduce((acc, curr) => {
      if (curr.operation && !acc[curr.operation.name]) {
        acc[curr.operation.name] = [];
      }
      if (curr.operation && curr.issues) {
        acc[curr.operation.name].push(curr.issues.name);
      }
      return acc;
    }, {} as Record<string, string[]>);
    return Object.entries(operationToIssuesListMap).map(([operation, issues]) => {
      return {
        operation,
        concatinatedIssues: _.uniq(issues).join(', '),
      };
    });
  },
  getMasterDataListName(appSyncItemList: TAppSyncScreenItem[], id: string) {
    for (let i = 0; i < appSyncItemList.length; i++) {
      if (
        appSyncItemList[i].type === 'ms_data' &&
        (appSyncItemList[i].id === id || appSyncItemList[i].value.includes(id))
      ) {
        return appSyncItemList[i].value;
      }
    }
    return 'default';
  },

  mergeUserLineMapWithBgLineMap(userLineMap: TLineMap, bgLineMap: TBgLineMap): TLineMap {
    //entries in bgLineMap always presetnt in userLineMap but may have missing counts
    Object.entries(bgLineMap).forEach(([line, bgLineMapItem]) => {
      userLineMap = produce(userLineMap, (draft) => {
        Object.entries(bgLineMapItem.bctxWipMap).forEach(([bctxKey, wipMapItem]) => {
          Object.entries(wipMapItem.sizeToWipMap).forEach(([size, sizeInfo]) => {
            if (
              sizeInfo.wip_qty !== 0 &&
              draft[line] &&
              draft[line].bctxWipMap[bctxKey] &&
              draft[line].bctxWipMap[bctxKey].sizeToWipMap[size]
            ) {
              draft[line].bctxWipMap[bctxKey].sizeToWipMap[size].wip_qty += sizeInfo.wip_qty;
            }
          });
        });
        Object.entries(bgLineMapItem.shiftMap).forEach(([shift, shiftMapItem]) => {
          Object.entries(shiftMapItem.clientBctx).forEach(([bctxKey, clientBctxItem]) => {
            Object.entries(clientBctxItem.sizeMap).forEach(([size, sizeInfo]) => {
              Object.entries(sizeInfo.client_work).forEach(([actualType, clientWork]) => {
                if (
                  clientWork.actuals !== 0 &&
                  draft[line] &&
                  draft[line].shiftMap[shift].clientBctx[bctxKey] &&
                  draft[line].shiftMap[shift].clientBctx[bctxKey].sizeMap[size]
                ) {
                  draft[line].shiftMap[shift].clientBctx[bctxKey].sizeMap[size].client_work[
                    actualType as TActualType
                  ].actuals += clientWork.actuals;
                }
              });
            });
          });
        });
      });
    });
    return userLineMap;
  },

  getBulkEventBackgroundMetadata(
    bulkEventWithoutStatus: TBulkEventWithoutStatus,
    eventsStatus: TBulkEventBackgroundData,
  ): TBulkEvent {
    switch (bulkEventWithoutStatus.type) {
      case 'BUNDLE_QC_COMPLETE_EVENT': {
        return {
          ...bulkEventWithoutStatus,
          backgroundMetadata: eventsStatus.backgroundMetadata,
          events: bulkEventWithoutStatus.events.map((event, index) => {
            return {
              ...event,
              eventBackgroundMetadata: eventsStatus.events[index],
            };
          }),
        };
      }
      case 'BUNDLE_QC_MISSING_EVENT': {
        return {
          ...bulkEventWithoutStatus,
          backgroundMetadata: eventsStatus.backgroundMetadata,
          events: bulkEventWithoutStatus.events.map((event, index) => {
            return {
              ...event,
              eventBackgroundMetadata: eventsStatus.events[index],
            };
          }),
        };
      }
      case 'BCTX_CHANGE_EVENT': {
        return {
          ...bulkEventWithoutStatus,
          backgroundMetadata: eventsStatus.backgroundMetadata,
          events: bulkEventWithoutStatus.events.map((event, index) => {
            return {
              ...event,
              eventBackgroundMetadata: eventsStatus.events[index],
            };
          }),
        };
      }
      case 'FORWARD_BULK_EVENT': {
        return {
          ...bulkEventWithoutStatus,
          backgroundMetadata: eventsStatus.backgroundMetadata,
          events: bulkEventWithoutStatus.events.map((event, index) => {
            return {
              ...event,
              eventBackgroundMetadata: eventsStatus.events[index],
            };
          }),
        };
      }
      case 'MEMBER_ADDITION_EVENT': {
        return {
          ...bulkEventWithoutStatus,
          backgroundMetadata: eventsStatus.backgroundMetadata,
          events: bulkEventWithoutStatus.events.map((event, index) => {
            return {
              ...event,
              eventBackgroundMetadata: eventsStatus.events[index],
            };
          }),
        };
      }
      case 'UNDO_BULK_EVENT': {
        return {
          ...bulkEventWithoutStatus,
          backgroundMetadata: eventsStatus.backgroundMetadata,
          events: bulkEventWithoutStatus.events.map((event, index) => {
            return {
              ...event,
              eventBackgroundMetadata: eventsStatus.events[index],
            };
          }),
        };
      }
      case 'ROAMING_QC_EVENT': {
        return {
          ...bulkEventWithoutStatus,
          backgroundMetadata: eventsStatus.backgroundMetadata,
          events: bulkEventWithoutStatus.events.map((event, index) => {
            return {
              ...event,
              eventBackgroundMetadata: eventsStatus.events[index],
            };
          }),
        };
      }
    }
  },

  getInitialStatusData(
    bulkEventsWithoutStatus: TBulkEventWithoutStatus,
    retryCount: number,
    eventIds: number[],
  ): TBulkEventBackgroundData {
    return {
      backgroundMetadata: {
        groupId: bulkEventsWithoutStatus.metadata.groupId,
        bulkEventStatus: 'YET_TO_BE_SYNCED',
        errorDetails: {},
        remainingRetriesCount: retryCount,
      },
      events: bulkEventsWithoutStatus.events.map((event, index) => {
        const eventBackgroundMetatData: TEventBackgroundMetadata = {
          v_id: event.eventInfo.v_id,
          status: 'YET_TO_BE_SYNCED',
          eventId: eventIds[index],
        };
        return eventBackgroundMetatData;
      }),
    };
  },

  getStatusInfoFromBulkEvents(bulkEvents: TBulkEvent[]): TBulkEventBackgroundData[] {
    return bulkEvents.map((bulkEvent) => {
      return {
        backgroundMetadata: bulkEvent.backgroundMetadata,
        events: bulkEvent.events.map((event) => event.eventBackgroundMetadata),
      };
    });
  },

  getBgDBEventsByGroupId(
    bgDBEvents: TBackgroundEventsSyncingDB['dailyCache']['events']['list'],
  ): Map<string, TBulkEventBackgroundData> {
    const bgDBEventByGroupId = new Map<string, TBulkEventBackgroundData>();
    bgDBEvents.forEach((bgDBEvent) => {
      if (!bgDBEventByGroupId.has(bgDBEvent.backgroundMetadata.groupId)) {
        bgDBEventByGroupId.set(bgDBEvent.backgroundMetadata.groupId, bgDBEvent);
      }
    });
    return bgDBEventByGroupId;
  },

  getEventIds(
    bulkEventsWithoutStatus: TUserActualsDB['dailyCache']['events']['list'],
    bgDBEventByGroupId: Map<string, TBulkEventBackgroundData>,
    oldNextEventId: number,
  ) {
    // Slot per userDB event: number[] (fresh ids) for events not yet in bgDB, undefined for events already in bgDB.
    // Index-aligned (sparse) because a missing event can be anywhere, not just at the end.
    const eventIdsByIndex: (number[] | undefined)[] = [];
    bulkEventsWithoutStatus.forEach((bulkEventWithoutStatus, index) => {
      if (!bgDBEventByGroupId.has(bulkEventWithoutStatus.metadata.groupId)) {
        const ids = [...Array(bulkEventWithoutStatus.events.length).keys()].map((i) => i + oldNextEventId);
        oldNextEventId += bulkEventWithoutStatus.events.length;
        eventIdsByIndex[index] = ids;
      }
    });
    return [eventIdsByIndex, oldNextEventId] as const;
  },

  // Matches each userDB event to its bgDB status by groupId (identity) instead of array position, so a
  // missing bgDB entry no longer shifts every later event onto the wrong status. Check [PBI-3211]
  getBulkEventsWithStatus(
    bulkEventsWithoutStatus: TUserActualsDB['dailyCache']['events']['list'],
    bgDBEvents: TBackgroundEventsSyncingDB['dailyCache']['events']['list'],
    retryCount: number,
    oldNextEventId: number,
  ) {
    const bgDBEventByGroupId = BCTXComputationHelper.getBgDBEventsByGroupId(bgDBEvents);
    const [eventIdsByIndex, newNextEventId] = BCTXComputationHelper.getEventIds(
      bulkEventsWithoutStatus,
      bgDBEventByGroupId,
      oldNextEventId,
    );
    const bulkEvents = bulkEventsWithoutStatus.map((bulkEventWithoutStatus, index) => {
      const matchedBgDBEvent = bgDBEventByGroupId.get(bulkEventWithoutStatus.metadata.groupId);
      if (matchedBgDBEvent) {
        return BCTXComputationHelper.getBulkEventBackgroundMetadata(bulkEventWithoutStatus, matchedBgDBEvent);
      } else {
        // Not in bgDB -> new event; eventIdsByIndex[index] is always defined here (set by getEventIds).
        return BCTXComputationHelper.getBulkEventBackgroundMetadata(
          bulkEventWithoutStatus,
          BCTXComputationHelper.getInitialStatusData(
            bulkEventWithoutStatus,
            retryCount,
            eventIdsByIndex[index] as number[],
          ),
        );
      }
    });
    return [bulkEvents, newNextEventId] as const;
  },

  // To show the new identifier for PO Split
  getVariableGroupFieldsInfo(selectedBctxInfo: TSelectedBctxInfo): TVariableGroupFieldsInfo[] {
    if (selectedBctxInfo.newBctxForPOSplit) {
      const newBctx = selectedBctxInfo.newBctxForPOSplit;
      return newBctx.groupedHeaderInfo.map((header) => {
        return {
          variableFieldName: header.displayName,
          currentVariableFieldValue:
            selectedBctxInfo.bctxInfoWithoutSize.list.find((item) => item.k === header.key)?.v || '',
          newVariableFieldValue: newBctx.bctx.list.find((item) => item.k === header.key)?.v || '',
        };
      });
    } else {
      return [];
    }
  },
  createInitialLineMapItemFromLineMapCreationItemList(
    lineMapCreationItemList: TLineMapCreationItems[],
    shiftId: string,
    selectedBctxItem: TSelectedBctxInfo,
  ): TLineMapItem {
    const clientBctx: TClient_BCTX = {};
    const bctxWipMap: TBctxWipMap = {};
    lineMapCreationItemList.forEach((lineMapCreationItems) => {
      const bctxItemListWithoutSize: TBctxItemWithoutSize[] = lineMapCreationItems.bctxItemListWithoutSize;
      const size: string = lineMapCreationItems.size;
      const bctx_key: string = lineMapCreationItems.bctx_key;
      const wip_qty: number = lineMapCreationItems.wip_qty;
      const sizeMapItem: TSizeMapItem = {
        name: size,
        client_work: BCTXComputationHelper.createInitialClientWork(),
      };
      const sizeInfo: TSizeInfo = {
        name: size,
        wip_qty: wip_qty,
      };
      if (clientBctx[bctx_key]) {
        clientBctx[bctx_key].sizeMap[sizeMapItem.name] = sizeMapItem;
      } else {
        clientBctx[bctx_key] = {
          bctx_key: bctx_key,
          sizeMap: {
            [sizeMapItem.name]: sizeMapItem,
          },
        };
      }
      if (bctxWipMap[bctx_key]) {
        bctxWipMap[bctx_key].sizeToWipMap[sizeInfo.name] = sizeInfo;
      } else {
        bctxWipMap[bctx_key] = {
          bctxKey: bctx_key,
          bctxItemListWithoutSize: bctxItemListWithoutSize,
          sizeToWipMap: {
            [sizeInfo.name]: sizeInfo,
          },
          filteredSizesList: [],
        };
      }
    });
    const lineMapItem: TLineMapItem = {
      bctxWipMap,
      selectedBctxInfo: selectedBctxItem,
      shiftMap: {
        [shiftId]: {
          clientBctx,
          rectifiedBCTXKeysInfo: {},
        },
      },
    };
    return lineMapItem;
  },

  getAllBctxItemsAcrossAllLinesAsRecord(lineMap: TLineMap, planBasedLineMap: TPlanBasedLineMap): TAllBctxItems {
    const acc = Object.values(lineMap).reduce((acc, curr) => {
      Object.entries(curr.bctxWipMap).forEach(([key, value]) => {
        if (!acc[key]) acc[key] = value.bctxItemListWithoutSize;
      });
      return acc;
    }, {} as Record<string, TBctxItemWithoutSize[]>);

    return Object.values(planBasedLineMap).reduce((acc, curr) => {
      Object.entries(curr).forEach(([key, value]) => {
        if (!acc[key]) acc[key] = value.bctxItemListWithoutSize;
      });
      return acc;
    }, acc);
  },

  getMasterDataListValue(
    listName: string,
    req_id: string,
    allMasterdataListsByBctx: TAllMasterdataListsByBctx,
    isDefaultMasterDataListAllowed: boolean = true,
  ): TMasterdataListResult {
    const details = allMasterdataListsByBctx[req_id];
    const defaultDetails = allMasterdataListsByBctx['default'];
    if (
      details &&
      details[listName] &&
      details[listName].masterdataList &&
      details[listName].masterdataList.list &&
      details[listName].masterdataList.list.length
    ) {
      return {
        status: 'SUCCESS',
        allList: details[listName].masterdataList.list,
        list:
          details[listName].filteredMasterdataList.length === 0
            ? details[listName].masterdataList.list
            : details[listName].masterdataList.list.filter(({ name }) =>
                details[listName].filteredMasterdataList.find((id) => id === name),
              ),
      };
    } else if (defaultDetails && defaultDetails[listName] && isDefaultMasterDataListAllowed) {
      return {
        status: 'SUCCESS',
        allList: defaultDetails[listName].masterdataList.list,
        list:
          defaultDetails[listName].filteredMasterdataList.length === 0
            ? defaultDetails[listName].masterdataList.list
            : defaultDetails[listName].masterdataList.list.filter(({ name }) =>
                defaultDetails[listName].filteredMasterdataList.find((id) => id === name),
              ),
      };
    } else {
      return {
        status: 'FAILURE',
        error: 'NOT_CONFIGURED',
      };
    }
  },

  getAllBctxItemsAcrossAllLinesForSationId(
    planBasedLineMap: TPlanBasedLineMap,
    lineMap: TLineMap,
    stationId: string,
  ): TAllBctxItems {
    const planBasedLineMapItem: TPlanBasedLineMapItem = planBasedLineMap[stationId];
    const lineMapItem: TLineMapItem = lineMap[stationId];
    const allBctxItemsForStationId: TAllBctxItems = {};
    if (lineMapItem) {
      Object.entries(lineMapItem.bctxWipMap).forEach(([key, value]) => {
        if (!allBctxItemsForStationId[key]) allBctxItemsForStationId[key] = value.bctxItemListWithoutSize;
      });
    }
    if (planBasedLineMapItem) {
      Object.entries(planBasedLineMapItem).forEach(([key, value]) => {
        if (!allBctxItemsForStationId[key]) allBctxItemsForStationId[key] = value.bctxItemListWithoutSize;
      });
    }

    return allBctxItemsForStationId;
  },

  createInitialLineMapItemForPlanBased(
    allBctxItemsForStationId: TAllBctxItems,
    appSyncItemList: TAppSyncScreenItem[],
    allMasterdataListsInfo: TAllMasterdataListsByBctx,
    selectedBctx: TSelectedBctxInfo,
    shiftId: string,
    app_identifier: TAppIdentifier | boolean,
  ): TLineMapItem {
    const lineMapCreationItems: TLineMapCreationItems[] = [];

    Object.entries(allBctxItemsForStationId).forEach(([bctx_key, bctxItemListWithoutSize]) => {
      const listName = BCTXComputationHelper.getMasterDataListName(appSyncItemList, 'size_list');
      let masterDataList: TMasterdataItemInfo[] = [];
      const masterdataListResult = BCTXComputationHelper.getMasterDataListValue(
        listName,
        bctx_key,
        allMasterdataListsInfo,
        app_identifier !== 'line_in_v2',
      );
      if (masterdataListResult.status === 'SUCCESS') {
        masterDataList = masterdataListResult.list;
      }

      const shouldUseEmptySize = app_identifier === 'line_in_v2' && masterDataList.length === 0;
      if (shouldUseEmptySize) {
        lineMapCreationItems.push({ bctxItemListWithoutSize, size: '', bctx_key, wip_qty: 0 });
      } else {
        masterDataList.forEach(({ name: size }) => {
          lineMapCreationItems.push({ bctxItemListWithoutSize, size, bctx_key, wip_qty: 0 });
        });
      }
    });

    return BCTXComputationHelper.createInitialLineMapItemFromLineMapCreationItemList(
      lineMapCreationItems,
      shiftId,
      selectedBctx,
    );
  },

  createInitialPlanBasedLineMap: (
    businessContextData: TRESPONSE_BusinessContextData,
    bctxKeysOrder: TBCTXKeysWithoutSize[],
  ): TPlanBasedLineMapItem => {
    const planBasedLineMapItem: TPlanBasedLineMapItem = {};
    businessContextData.forEach((data) => {
      const bctxItemListWithoutSize = BCTXComputationHelper.filterBctxItemsWithKeys(bctxKeysOrder, data.bctx_items);
      const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrder, bctxItemListWithoutSize);
      planBasedLineMapItem[bctxKey] = {
        bctxItemListWithoutSize,
        bctxKey,
      };
    });

    return planBasedLineMapItem;
  },

  getAppSyncItem(appSyncItemList: TAppSyncScreenItem[], id: string) {
    return appSyncItemList.find(
      (appSyncItem) => appSyncItem.type === 'ms_data' && (appSyncItem.id === id || appSyncItem.value.includes(id)),
    );
  },

  getMergedPlanBasedLineMap: (
    currentDB: TUserActualsDB['dailyCache'],
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    planLineMapItem: TPlanBasedLineMapItem,
  ) => {
    const { stationId } = selectedStationAndShiftId;
    if (!currentDB.planBasedLineMap[stationId]) {
      return produce(currentDB, (draft) => {
        draft.planBasedLineMap[stationId] = planLineMapItem;
      });
    } else {
      return produce(currentDB, (draft) => {
        Object.entries(planLineMapItem).forEach(([bctx_key, bctxPlanBasedItem]) => {
          draft.planBasedLineMap[stationId][bctx_key] = bctxPlanBasedItem;
        });
      });
    }
  },

  getLIneMapItemUpdatedWithInactiveBctxAndSize(
    activeBctxKeys: string[],
    lineMapItem: TLineMapItem,
    appSyncItemList: TAppSyncScreenItem[],
    allMasterdataListsInfo: TAllMasterdataListsByBctx,
    shiftId: string,
    app_identifier: TAppIdentifier | boolean,
  ): TLineMapItem {
    if (lineMapItem) {
      lineMapItem = produce(lineMapItem, (draft) => {
        if (lineMapItem.bctxWipMap) {
          Object.values(draft.bctxWipMap).forEach((item) => {
            const activeBctx = activeBctxKeys.find((key) => key === item.bctxKey);
            if (!activeBctx) {
              // setting isInactive = true because its deleted from admin
              draft.bctxWipMap[item.bctxKey].isInactive = true;
            } else {
              draft.bctxWipMap[item.bctxKey].isInactive = false;
            }
          });
        }
        if (lineMapItem.shiftMap && lineMapItem.shiftMap[shiftId].clientBctx) {
          Object.values(lineMapItem.shiftMap[shiftId].clientBctx).forEach((item) => {
            const listName = BCTXComputationHelper.getMasterDataListName(appSyncItemList, 'size_list');
            let activeSizeList: TMasterdataItemInfo[] = [];
            const masterdataListResult = BCTXComputationHelper.getMasterDataListValue(
              listName,
              item.bctx_key,
              allMasterdataListsInfo,
              app_identifier !== 'line_in_v2',
            );
            if (masterdataListResult.status === 'SUCCESS') {
              activeSizeList = masterdataListResult.list;
            }
            Object.values(item.sizeMap).forEach((sizeMapItem) => {
              const activeSize = activeSizeList.find((size) => size.name === sizeMapItem.name);
              if (!activeSize) {
                // setting isInactive = true because size is deleted from admin, it exist here  due to merging logic
                draft.shiftMap[shiftId].clientBctx[item.bctx_key].sizeMap[sizeMapItem.name].isInactive = true;
              } else {
                draft.shiftMap[shiftId].clientBctx[item.bctx_key].sizeMap[sizeMapItem.name].isInactive = false;
              }
            });
          });
        }
      });
    }
    return lineMapItem;
  },

  getActiveBctxKeys(lineMapItem: TLineMapItem, planBasedLineMapItem: TPlanBasedLineMapItem) {
    // if plan based is there that means,  bctxs are fetched, so all are active
    if (planBasedLineMapItem) {
      return Object.keys(planBasedLineMapItem);
    }
    // if bctx is IsInactive that means its deleted but exist here due to merging logic
    const activeBctxKeys: string[] = [];
    if (lineMapItem && lineMapItem.bctxWipMap) {
      Object.values(lineMapItem.bctxWipMap).forEach((item) => {
        if (!item.isInactive) {
          activeBctxKeys.push(item.bctxKey);
        }
      });
    }
    return activeBctxKeys;
  },
  isSameBctxCombination(bctxEle1: TBctxItemWithoutSize[], bctxEle2: TBctxItemWithoutSize[]) {
    if (bctxEle1.length === 0 || bctxEle2.length === 0) {
      return false;
    }
    return bctxEle1.every(
      (bctxItem1) =>
        ['search_id', 'display_piece_tag'].includes(bctxItem1.k) ||
        bctxEle2.some((bctxItem2) => bctxItem2.k === bctxItem1.k && bctxItem2.v === bctxItem1.v),
    );
  },

  updateCurrentDbBulkMdpointEvents(currentDB: TUserActualsDB, bulkEvents: TBulkEvent[]) {
    if (!bulkEvents.length) {
      return currentDB;
    }
    const relevantEvents: TSingleCountEvent[] = [];
    bulkEvents.forEach((bulkEvent) => {
      if (!bulkEvent.events) return;
      bulkEvent.events.forEach((event) => {
        if (event.type === 'FORWARD_EVENT' || event.type === 'UNDO_EVENT') {
          relevantEvents.push(event as TSingleCountEvent);
        }
      });
    });

    if (!relevantEvents.length) {
      return currentDB;
    }

    return produce(currentDB, (draft) => {
      draft.dailyCache.bulkMdPointEvents.unshift(...relevantEvents);
    });
  },
};
